/**
 * Panel lateral de auditoría de un repartidor (read-only).
 *
 * Se abre desde la lista "Repartidores que necesitan atención" / "Mejores del
 * período" de DeliveryQualityNext al clickear una fila. El repartidor seleccionado
 * vive en la URL (?driver=<key>) vía useUrlState, así que es deep-linkable y el
 * botón Atrás lo cierra (mismo patrón que LiveTracking). NO es un modal bloqueante
 * (respeta el ban "NO modal-first" del rediseño): no hay scrim, la lista queda
 * clickeable al lado y se puede saltar de un repartidor a otro.
 *
 * Todo lo que muestra son LECTURAS (getDriverBreakdown / getDriverHistory /
 * getTimeline / getScoreByPackage). Cero escrituras: en qa2 = PROD no se muta nada.
 *
 * Tres niveles de auditoría:
 *  1) Cabecera: nombre, score, nº evaluaciones, peores criterios.
 *  2) Por criterio (getDriverBreakdown.by_criterion) — peor primero.
 *  3) Tendencia del score en el tiempo (getTimeline con deliverer_id).
 *  4) Entregas (getDriverHistory) en bandas Buenas/Regulares/Malas; cada paquete
 *     abre el detalle IA (PackageAiDetailModal) con el "por qué" por sub-aspecto
 *     (etiqueta / domicilio / receptor), evidencia y fotos.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import dayjs from 'dayjs';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import deliveryQualityService from 'services/deliveryQualityService';
import { Skeleton } from 'components/ui/Skeleton';
import { EmptyState } from 'components/ui/EmptyState';
import { Badge } from 'components/ui/Badge';
import { cn } from 'utils/cn';
import { formatNumberCL, formatPercent } from 'utils/formatters';
import DriverPackagesBuckets from './DriverPackagesBuckets';
import PackageAiDetailModal from './PackageAiDetailModal';

// Etiquetas por criterio. by_criterion (breakdown) usa estas claves; mantenido
// local y minimal para no acoplar el panel al CRITERION_META de la página.
const CRITERION_LABEL = {
  image_count: 'Cantidad de fotos',
  images_ai: 'Análisis IA de fotos',
  timeliness: 'Puntualidad',
  distance: 'Distancia al punto',
  receiver: 'Nombre del receptor',
  rut: 'RUT del receptor',
};

const scoreTone = (score) => (score < 70 ? 'critical' : score < 85 ? 'warn' : 'good');

// Hook de fetch local: mismo contrato que el useEndpoint de la página (desempaca
// res.data, cancela en cleanup). Reimplementado acá para no exportar el de la página.
function useDriverEndpoint(factory, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(() => factory())
      .then((res) => {
        if (cancelled) return;
        setState({ data: res?.data ?? res ?? null, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ data: null, loading: false, error: err?.message || 'error' });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

export default function DriverAuditPanel({ driverId, driverRow, filters, isDark, onClose }) {
  const { t } = useTranslation('deliveryquality');
  // Paquete seleccionado para abrir el detalle IA: { id (package _id), num }.
  const [pkg, setPkg] = useState(null);

  // ESC cierra. NO bloqueamos el scroll del body ni ponemos scrim: el panel no es
  // modal y la lista detrás debe seguir interactuable.
  // Guard `!pkg`: el detalle de paquete (PackageAiDetailModal) tiene su PROPIO
  // handler de ESC en window, y stopPropagation NO frena a un co-listener del
  // mismo target (window). Sin el guard, un ESC para cerrar el modal cerraría
  // también todo el panel. Con modal abierto, dejamos que el modal maneje el ESC.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !pkg) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pkg]);

  const dateScope = useMemo(
    () => ({ date_from: filters.date_from, date_to: filters.date_to }),
    [filters.date_from, filters.date_to],
  );

  const breakdown = useDriverEndpoint(
    () => deliveryQualityService.getDriverBreakdown(driverId, dateScope),
    [driverId, dateScope],
  );
  const history = useDriverEndpoint(
    () => deliveryQualityService.getDriverHistory(driverId, { ...dateScope, page: 1, limit: 200 }),
    [driverId, dateScope],
  );
  const timeline = useDriverEndpoint(
    () => deliveryQualityService.getTimeline({ ...filters, deliverer_id: driverId, granularity: 'day' }),
    [driverId, filters],
  );

  const name =
    driverRow?.user?.fullName ||
    [driverRow?.user?.name, driverRow?.user?.lastName].filter(Boolean).join(' ') ||
    driverRow?.user?.email ||
    t('Repartidor');
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase();

  const score = driverRow?.avg_score ?? breakdown.data?.avg_total_score ?? 0;
  const evals = driverRow?.total_evaluations ?? breakdown.data?.total_evaluations ?? 0;
  const tone = scoreTone(score);
  const topIssues = breakdown.data?.top_issues || [];

  const packages = history.data?.data || [];

  return (
    <aside className="dq-audit" aria-label={t('Auditoría del repartidor')}>
      <header className="dq-audit__header">
        <span className={cn('dq-driver__avatar', `dq-driver__avatar--${tone}`)} aria-hidden>
          {initials || '··'}
        </span>
        <div className="dq-audit__heading">
          <span className="dq-audit__name" title={name}>{name}</span>
          <span className="dq-audit__meta">
            <strong className={cn('dq-audit__score', `dq-audit__score--${tone}`)}>{score.toFixed(1)}</strong>
            {' '}{t('score')} · {formatNumberCL(evals)} {t('evaluadas')}
          </span>
          {topIssues.length > 0 && (
            <div className="dq-audit__chips">
              <span className="dq-audit__chips-label">{t('Peores:')}</span>
              {topIssues.slice(0, 3).map((k) => (
                <Badge key={k} tone="warning">{t(CRITERION_LABEL[k] || k)}</Badge>
              ))}
            </div>
          )}
        </div>
        <button type="button" className="dq-audit__close" onClick={onClose} aria-label={t('Cerrar')}>
          <X className="w-4 h-4" />
        </button>
      </header>

      <div className="dq-audit__body">
        {/* ── Por criterio ─────────────────────────────────────────────── */}
        <section>
          <h3 className="dq-audit__section-title">{t('Por dónde pierde calidad')}</h3>
          <CriteriaBreakdown state={breakdown} t={t} />
        </section>

        {/* ── Tendencia ────────────────────────────────────────────────── */}
        <section>
          <h3 className="dq-audit__section-title">{t('Tendencia del score')}</h3>
          <MiniTrend state={timeline} isDark={isDark} t={t} />
        </section>

        {/* ── Entregas (lista auditable) ───────────────────────────────── */}
        <section>
          <h3 className="dq-audit__section-title">{t('Entregas evaluadas')}</h3>
          {history.loading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
            </div>
          ) : (
            <DriverPackagesBuckets
              packages={packages}
              onSelect={(id, num) => setPkg({ id, num })}
            />
          )}
        </section>
      </div>

      <PackageAiDetailModal
        isOpen={!!pkg}
        packageId={pkg?.id}
        followingNumber={pkg?.num}
        toggle={() => setPkg(null)}
      />
    </aside>
  );
}

// ── Desglose por criterio (peor primero) ─────────────────────────────────────
function CriteriaBreakdown({ state, t }) {
  const { data, loading, error } = state;
  if (loading) return <Skeleton className="h-40 rounded-xl" />;
  if (error || !data || !data.by_criterion) {
    return (
      <EmptyState
        title={t('Sin desglose por criterio')}
        description={t('No hay evaluaciones de este repartidor en el rango.')}
      />
    );
  }
  const total = data.total_evaluations || 0;
  const rows = Object.entries(data.by_criterion)
    .filter(([key]) => CRITERION_LABEL[key])
    .map(([key, c]) => {
      const passRate = (c?.pass_rate ?? 0) * 100;
      const fails = Math.max(0, total - Math.round((c?.pass_rate ?? 0) * total));
      return { key, label: CRITERION_LABEL[key], passRate, fails };
    })
    .sort((a, b) => a.passRate - b.passRate);

  if (rows.length === 0) return null;

  return (
    <div className="dq-criteria">
      {rows.map((r) => {
        const tone = r.passRate < 80 ? 'critical' : r.passRate < 90 ? 'warn' : 'good';
        return (
          <div key={r.key} className={cn('dq-criteria__row', `dq-criteria__row--${tone}`)}>
            <div className="dq-criteria__main">
              <div className="dq-criteria__head">
                <span className="dq-criteria__label">{t(r.label)}</span>
              </div>
              <div className="dq-criteria__bar" aria-hidden>
                <div className="dq-criteria__bar-fill" style={{ width: `${r.passRate.toFixed(1)}%` }} />
              </div>
            </div>
            <div className="dq-criteria__stats">
              <div className="dq-criteria__rate">{r.passRate.toFixed(0)}%</div>
              <div className="dq-criteria__fails">{formatNumberCL(r.fails)} {t('fallaron')}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tendencia del score del repartidor ───────────────────────────────────────
// Mismo contrato que el TrendChart de la página: el campo de fecha es `bucket`,
// banda Y útil [70,100] con piso escalonado si cae bajo el umbral.
function MiniTrend({ state, isDark, t }) {
  const { data, loading, error } = state;
  if (loading) return <Skeleton className="h-44 rounded-xl" />;
  if (error || !data) return null;

  const series = (data.points || data.timeline || data.data || data || [])
    .map((p) => {
      const bucket = p.bucket || p.date || p.bucket_date || p.day;
      return {
        bucket: String(bucket ?? ''),
        date: dayjs(bucket).format('DD MMM'),
        score: Number((p.avg_score ?? p.score ?? 0).toFixed(1)),
      };
    })
    .sort((a, b) => a.bucket.localeCompare(b.bucket));

  if (series.length === 0) {
    return (
      <EmptyState
        title={t('Sin tendencia')}
        description={t('Aún no hay puntos suficientes en el rango.')}
      />
    );
  }

  const minScore = Math.min(...series.map((p) => p.score));
  const yFloor = minScore < 70 ? Math.max(0, Math.floor(minScore / 10) * 10) : 70;
  const stroke = 'var(--color-primary, #F97316)';
  const grid = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const tick = 'rgb(var(--fg-muted))';

  return (
    <div className="dq-trend">
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={series} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="dqAuditTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.18} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis dataKey="date" tick={{ fill: tick, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis domain={[yFloor, 100]} tick={{ fill: tick, fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{
              background: 'var(--surface-primary)',
              border: '1px solid var(--border-secondary)',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: 'rgb(var(--fg))' }}
          />
          <Area
            type="monotone"
            dataKey="score"
            stroke={stroke}
            strokeWidth={2}
            fill="url(#dqAuditTrendFill)"
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
